import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, Stack, Typography, IconButton, Tooltip } from "@mui/material";
import { ReactComponent as TagEmail } from "../../../assets/images/ChatImages/tagemail.svg";
import { ReactComponent as Download } from "../../../assets/images/ChatImages/download.svg";
import { ReactComponent as Add } from "../../../assets/images/ChatImages/add.svg";
import { ReactComponent as PendingUser } from "../../../assets/images/ChatImages/profilechat.svg";
import { ReactComponent as DotsMenu } from "../../../assets/images/ChatImages/dotsmenu.svg";
import { CV2_COLUMN_HEADER_HEIGHT } from "../../../constants/chatV2Constants";
import { visitorAppearsOnline } from "../../../utils/index";
import { isJoinNoticeMessage } from "../../../utils/messageNotification";
import { IconDetailsSplit24 } from "./ChatV2Icons";
import ChatOverflowMenu from "./ChatOverflowMenu";

function formatTranscriptTimestamp(timestamp) {
  if (!timestamp) return "N/A";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });
}

function ChatHeaderV2({
  selectUser,
  customerData,
  messages = [],
  latestMsgTime,
  setTicketPopup,
  setTranscriptPopup,
  setAssignChatPopup,
  archivedRoom,
  onBanVisitor,
  detailsOpen = true,
  onToggleDetails,
}) {
  const [chatMenu, setChatMenu] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setChatMenu(false);
      }
    };
    if (chatMenu) window.addEventListener("click", handleOutsideClick);
    return () => window.removeEventListener("click", handleOutsideClick);
  }, [chatMenu]);

  const formatTime = (time) =>
    `${new Date().toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })} ${time}`;

  const safeSelectUserTime = typeof selectUser?.time === "string" ? selectUser.time : "";
  const displayedTime =
    latestMsgTime == null
      ? safeSelectUserTime.length === 8
        ? formatTime(safeSelectUserTime)
        : safeSelectUserTime
      : latestMsgTime;

  const locationLabel = customerData?.[0]?.location || "";
  // Same presence source as the chat list dot, so the two never disagree.
  const visitorOnline = visitorAppearsOnline(selectUser);
  const peopleStatus = String(
    customerData?.[0]?.people_status || ""
  ).toLowerCase();
  const showBanVisitor =
    peopleStatus !== "banned" &&
    peopleStatus !== "blocked" &&
    !customerData?.[0]?.banned_until;

  const handleArchive = () => {
    setChatMenu(false);
    // Parent emits archive sockets, clears selection, removes from list, and
    // updates counts while staying on the current tab (does not jump to Closed).
    archivedRoom?.();
  };

  const handleBan = () => {
    setChatMenu(false);
    onBanVisitor?.();
  };

  const handleDownloadTranscript = useCallback(() => {
    const visitorName =
      customerData?.[0]?.name || selectUser?.title || "Visitor";
    const visitorEmail = customerData?.[0]?.email || "";

    const publicMessages = (Array.isArray(messages) ? messages : []).filter(
      (message) => !message?.isPrivate && !isJoinNoticeMessage(message)
    );

    const messageBlocks = publicMessages
      .map((message) => {
        const sender = message.isAgent
          ? message.agent?.full_name || message.sender_name || "Agent"
          : message.sender_name || visitorName || "Guest";
        const body =
          message.un_trimmed_content || message.content || message.message || "";
        if (!String(body).trim()) return null;
        return `${sender} (${formatTranscriptTimestamp(
          message.createdAt || message.created_at
        )})\n${body}`;
      })
      .filter(Boolean)
      .join("\n-------------------------------------------\n");

    const transcriptText = [
      "Chat transcript:",
      "-------------------------------------------",
      `Name: ${visitorName}`,
      `E-mail: ${visitorEmail || "N/A"}`,
      "-------------------------------------------",
      messageBlocks || "No messages available",
      "",
    ].join("\n");

    const roomSuffix =
      String(selectUser?.id || selectUser?._id || "chat").slice(-6) || "chat";
    const element = document.createElement("a");
    const file = new Blob([transcriptText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(file);
    element.href = url;
    element.download = `chat-transcript-${roomSuffix}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(url);
  }, [customerData, messages, selectUser]);

  if (!selectUser) return null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        height: CV2_COLUMN_HEADER_HEIGHT,
        minHeight: CV2_COLUMN_HEADER_HEIGHT,
        flexShrink: 0,
        px: 2,
        borderBottom: "1px solid #E5E9EC",
        boxSizing: "border-box",
        gap: 2,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 600,
            color: "#061522",
            lineHeight: "24px",
            textTransform: "capitalize",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selectUser.title || "Visitor"}
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.75} sx={{ mt: 0.25 }}>
          <Typography sx={{ fontSize: 10, color: "#405261", lineHeight: "16px" }}>
            {displayedTime || " "}
          </Typography>
          {locationLabel ? (
            <>
              <Box
                title={visitorOnline ? "Active" : "Inactive"}
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  bgcolor: visitorOnline ? "#389E0D" : "#D5DCE1",
                  flexShrink: 0,
                  alignSelf: "center",
                }}
              />
              <Typography
                sx={{
                  fontSize: 10,
                  color: "#405261",
                  lineHeight: "16px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {locationLabel}
              </Typography>
            </>
          ) : null}
        </Stack>
      </Box>

      <Stack direction="row" alignItems="center" gap={1.5} ref={dropdownRef} sx={{ position: "relative", flexShrink: 0 }}>
        <Tooltip title="Send Transcript" placement="top">
          <IconButton size="small" onClick={() => setTranscriptPopup?.(true)} sx={{ p: 0, width: 24, height: 24 }}>
            <TagEmail />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download Transcript" placement="top">
          <IconButton
            size="small"
            onClick={handleDownloadTranscript}
            sx={{ p: 0, width: 24, height: 24 }}
            aria-label="Download transcript"
          >
            <Download />
          </IconButton>
        </Tooltip>
        <Tooltip title="Create Ticket" placement="top">
          <IconButton size="small" onClick={() => setTicketPopup?.(true)} sx={{ p: 0, width: 24, height: 24 }}>
            <Add />
          </IconButton>
        </Tooltip>
        {selectUser?.id && (
          <Tooltip title="Assign Chat" placement="top">
            <IconButton size="small" onClick={() => setAssignChatPopup?.(true)} sx={{ p: 0, width: 24, height: 24 }}>
              <PendingUser />
            </IconButton>
          </Tooltip>
        )}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setChatMenu((v) => !v);
          }}
          sx={{ p: 0, width: 24, height: 24 }}
          aria-label="Chat options"
        >
          <DotsMenu />
        </IconButton>
        {!detailsOpen && (
          <IconButton
            size="small"
            aria-label="Open details"
            onClick={onToggleDetails}
            sx={{ p: 0, width: 24, height: 24 }}
          >
            <IconDetailsSplit24 />
          </IconButton>
        )}
        <ChatOverflowMenu
          open={chatMenu && !!selectUser?.id}
          onBan={handleBan}
          onEndChat={handleArchive}
          showBan={showBanVisitor}
          showEndChat={!selectUser?.is_archived}
        />
      </Stack>
    </Stack>
  );
}

export default ChatHeaderV2;
